import type { MouseEventHandler, PropsWithChildren } from 'react'
import { Fragment, useEffect, useRef, useState } from 'react'
import type { Column, Row, TableInstance, TableOptions } from 'react-table'
import { useSortBy, useTable } from 'react-table'

import classnames from 'classnames'
import classNames from 'classnames'

import { Icon } from '../Icon/Icon'
import Text from '../Text/Text'

import styles from './Table.module.css'

export const ExtendedTableContent = ({
  content,
  setExpandedState,
}: {
  content: any
  setExpandedState: any
}) => {
  return (
    <div className={styles.expandedContainer}>
      <div className={styles.expandedContent}>
        <Icon type="InfoIcon" />

        {content}
      </div>

      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.closeIcon}
        onClick={setExpandedState}
      >
        <path
          d="M12 12L4 4M4 12L12 4"
          stroke="#666666"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

export type ExpandableColumn<T> = Column & {
  isExpandable?: boolean
  expandableText?: string | React.ReactNode
  isSortable?: boolean
  isSorted?: boolean
  isSortedDesc?: boolean
  sortProps?: {
    isSorted: boolean
    isSortedDesc: boolean
  }
}

export interface TableProps<T extends Record<string, unknown>>
  extends TableOptions<T> {
  testID?: string
  name: string
  noMarginTop?: boolean
  onAdd?: (instance: TableInstance<T>) => MouseEventHandler
  onDelete?: (instance: TableInstance<T>) => MouseEventHandler
  onEdit?: (instance: TableInstance<T>) => MouseEventHandler
  onClick?: (row: Row<T>) => void
  minWidth?: number
  minHeight?: number
  isTableView?: boolean
  renderCard?: (props: any) => React.ReactNode
  expandedContent?: (row: Row<T>) => React.ReactNode
  columns: any[]
  isMyPortfolio?: boolean
  onSort?: () => void
}

export default function Table<T extends Record<string, unknown>>({
  testID,
  columns,
  defaultColumn,
  noMarginTop = false,
  minWidth,
  minHeight,
  isTableView = true,
  isMyPortfolio = false,
  renderCard,
  expandedContent,
  ...props
}: PropsWithChildren<TableProps<T>>) {
  const tableRef = useRef<any>(null)
  const [useFade, setUseFade] = useState<boolean>(false)

  const [expandedState, setExpandedState] = useState<{
    rowId: string | null
    columnId: string | null
  }>({ rowId: null, columnId: null })

  const instance = useTable<T>(
    {
      ...props,
      columns,
      defaultColumn,
    },
    useSortBy
  )

  const { getTableProps, headerGroups, getTableBodyProps, prepareRow, rows } =
    instance

  useEffect(() => {
    const isFadeRequired = () => {
      const el = tableRef.current

      const isScrolledToRight =
        el?.scrollLeft === el?.scrollWidth - el?.clientWidth
      setUseFade(!isScrolledToRight)
    }

    isFadeRequired()
    tableRef.current?.addEventListener('scroll', isFadeRequired)

    return () => {
      tableRef.current?.removeEventListener('scroll', isFadeRequired)
    }
  }, [])

  const renderTableRowFooter = (row: any) => {
    if (!row.original.tableRowFooter) return null

    return (
      <tr className={styles.tradingFloorRow}>
        <td
          colSpan={row.cells.length}
          className={styles.tradingFloorContentWrapper}
        >
          <div className={styles.tradingFloorContent}>
            {row.original.tableRowFooter}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div>
      <div
        className={classnames(
          styles.base,
          noMarginTop && styles.baseNoMarginTop,
          useFade && styles.faded
        )}
        data-testid={testID}
        ref={tableRef}
      >
        <table
          {...getTableProps()}
          className={classNames(styles.table, {
            [styles.oddStyling]: isMyPortfolio,
          })}
          style={{
            ...(minWidth && { minWidth: `${minWidth}px` }),
            ...(minHeight && isTableView && { minHeight: `${minHeight}px` }),
          }}
        >
          {isTableView ? (
            <thead>
              {headerGroups.map((headerGroup: any) => {
                const { key: headerGroupKey, ...getHeaderGroupProps } =
                  headerGroup.getHeaderGroupProps()
                return (
                  <tr {...getHeaderGroupProps} key={headerGroupKey}>
                    {headerGroup.headers.map((column: any) => {
                      const isSorted = column.sortProps?.isSorted
                      const isSortedDesc = column.sortProps?.isSortedDesc

                      return (
                        <th
                          {...column.getHeaderProps()}
                          style={{
                            width: column.width,
                            minWidth: column.minWidth,
                            maxWidth: column.maxWidth,
                            fontWeight: isSorted ? 'bold' : 'normal',
                            cursor: column.isSortable ? 'pointer' : 'default',
                          }}
                          key={`th-${column.id}`}
                          className={classnames(
                            isSorted && styles.columnActive,
                            column.isSortable && styles.sortable
                          )}
                          onClick={() => {
                            if (column.isSortable && props.onSort) {
                              props.onSort()
                            }
                          }}
                        >
                          <>
                            {column.render('Header')}
                            <span style={{ marginLeft: 16 }}>
                              {isSorted ? (
                                isSortedDesc ? (
                                  <div
                                    style={{
                                      transform: 'rotate(90deg)',
                                      display: 'inline-block',
                                    }}
                                  >
                                    <Icon type="Arrow" height={12} width={12} />
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      transform: 'rotate(270deg)',
                                      display: 'inline-block',
                                    }}
                                  >
                                    <Icon type="Arrow" height={12} width={12} />
                                  </div>
                                )
                              ) : (
                                ''
                              )}
                            </span>
                          </>
                        </th>
                      )
                    })}
                  </tr>
                )
              })}
            </thead>
          ) : null}
          {isTableView && (
            <tbody {...getTableBodyProps()}>
              {rows.map((row, rowIndex) => {
                prepareRow(row)
                const { key: rowKey, ...rowProps } = row.getRowProps()
                return (
                  <Fragment key={row.id || rowKey || rowIndex}>
                    <tr
                      {...rowProps}
                      key={rowKey}
                      className={classNames({
                        [styles.hasFooter]: row.original.tableRowFooter,
                      })}
                      data-test="table-row-item"
                    >
                      {row.cells.map((cell: any) => {
                        return (
                          <td
                            {...cell.getCellProps()}
                            key={cell.column.id}
                            onClick={() => {
                              if (
                                cell.column.isExpandable &&
                                cell.row.original.canExpand
                              ) {
                                setExpandedState(current => ({
                                  rowId:
                                    current.rowId === row.id &&
                                    current.columnId === cell.column.id
                                      ? null
                                      : row.id,
                                  columnId:
                                    current.rowId === row.id &&
                                    current.columnId === cell.column.id
                                      ? null
                                      : cell.column.id,
                                }))
                              }
                            }}
                            className={classnames({
                              [styles.expandable]:
                                cell.column.isExpandable &&
                                cell.row.original.canExpand,
                            })}
                          >
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                    {renderTableRowFooter(row)}
                    {/* {renderTableRowFooter(row)} */}
                    {expandedState.rowId === row.id && (
                      <tr className={styles.expandedContentRow}>
                        <td colSpan={row.cells.length}>
                          <ExtendedTableContent
                            content={
                              (row.cells as any[]).find(
                                cell =>
                                  cell.column.id === expandedState.columnId
                              )?.column.expandableText ? (
                                <Text>
                                  {
                                    (row.cells as any[]).find(
                                      cell =>
                                        cell.column.id ===
                                        expandedState.columnId
                                    )?.column.expandableText
                                  }
                                </Text>
                              ) : (
                                expandedContent && expandedContent(row)
                              )
                            }
                            setExpandedState={() =>
                              setExpandedState({
                                rowId: null,
                                columnId: null,
                              })
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          )}
        </table>
      </div>
      {!isTableView && (
        <div
          className={styles.cardStyleListing}
          data-test="table-row-card-item"
        >
          {rows.map(row => {
            prepareRow(row)
            const props = row.cells[0].row.original

            return renderCard ? (
              <div key={`portfolio-item-${row.id}`}>{renderCard(props)}</div>
            ) : null
          })}
        </div>
      )}
    </div>
  )
}
